import type { FC } from "react";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "flowbite-react";
import { useUserSession } from "../../../context/UserContext";
import { useTranslation } from "react-i18next";
import {
  HiOutlinePlus,
  HiSwitchHorizontal,
  HiPencilAlt,
  HiTrash,
} from "react-icons/hi";
import {
  Button,
  DefaultSkeleton,
  TableSkeleton,
  StandardTable,
  WorkspaceModal,
  DialogForm,
} from "@components";
import {
  ITableRow,
  IWorkspace,
  IWorkspaceBase,
  Timestamp,
  WorkspaceRole,
} from "../../../types";
import { useUserWorkspaces } from "../../../hooks";
import { useWorkspace } from "../../../context/WorkspaceContext";
import { useCache } from "../../../context/CacheContext";
import {
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
} from "../../../api";
import { WorkspaceAccess } from "../../../models";
import { SettingsRowProps } from "../components/SettingsRow";

// Matches the onboarding wizard's DEFAULT_WORKSPACE_NAME so a freshly created
// workspace is treated as "unnamed": the wizard's first step then shows an empty
// name field and prompts the user to name it, instead of pre-filling a
// throwaway default.
const NEW_WORKSPACE_DEFAULT_NAME = "Default workspace";

interface MyWorkspacesSettingsContentProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
}

const MyWorkspacesSettingsContent: FC<MyWorkspacesSettingsContentProps> = ({
  SettingsRow,
}) => {
  const [isUpsertModalOpen, openUpsertodal] = useState(false);
  const [isDeleteModalOpen, openDeleteModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { userSession } = useUserSession();
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const { updateCacheKey } = useCache();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(true);
  const [tableRows, setTableRows] = useState([] as ITableRow[]);
  const query = useUserWorkspaces(userSession?.user.email ?? "");
  const { t } = useTranslation(["common", "workspace"]);

  // Creating a workspace should feel like starting a brand-new setup: create it,
  // switch straight into it, and land in the onboarding wizard. The wizard owns
  // naming (step 1) and company setup, and its header account menu lets the user
  // switch back to an existing workspace at any point, so nothing is a dead end.
  const handleCreateWorkspace = async () => {
    if (isCreating || !userSession) return;
    setIsCreating(true);
    try {
      const body = await createWorkspace(userSession.token.accessToken, {
        name: NEW_WORKSPACE_DEFAULT_NAME,
      });
      const created = body?.data as IWorkspace | undefined;
      if (!created?.id) {
        throw new Error("Workspace create response missing id");
      }

      // Refresh the workspace list so the new workspace and the switcher in the
      // onboarding header stay in sync with the server.
      updateCacheKey();

      setActiveWorkspace({
        workspaceId: created.id,
        workspaceName: created.name ?? NEW_WORKSPACE_DEFAULT_NAME,
        name: userSession.user.email ?? "",
        email: userSession.user.email ?? "",
        role: WorkspaceRole.Owner,
        isApiKey: false,
        createdBy: created.createdBy ?? userSession.user.email ?? null,
      });
      navigate("/onboarding");
    } catch (error) {
      console.error("Failed to create workspace", error);
      // Keep the user on the page so they can retry; only reset on failure since
      // a success navigates away and unmounts this component.
      setIsCreating(false);
    }
  };

  const isQueryLoading = useMemo(
    () => query.isLoading || query.isRefetching,
    [query],
  );

  const queryData = useMemo(() => query.data ?? [], [query]);

  useEffect(() => {
    if (!isQueryLoading) {
      setLoading(false);
      // Sort workspaces by creation date (newest first)
      const sortedWorkspaces = queryData.sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.seconds - a.createdAt.seconds;
      });

      setTableRows(
        sortedWorkspaces.map((workspace) => {
          return {
            id: workspace.workspaceId,
            isActive: workspace.workspaceId === activeWorkspace?.workspaceId,
            cells: [
              {
                id: "name",
                children: workspace.workspaceName,
                showOnSmallScreen: true,
              },
              {
                id: "createdAt",
                children: Timestamp.toDisplayDate(workspace.createdAt),
              },
            ],
            actions: [
              {
                id: "switch",
                icon: <HiSwitchHorizontal />,
                onActionClick: () => setActiveWorkspace(workspace),
              },
              {
                id: "edit",
                icon: <HiPencilAlt />,
                onActionClick: () => {
                  setActiveWorkspace(workspace);
                  openUpsertodal(true);
                },
              },
              {
                id: "delete",
                icon: <HiTrash />,
                color: "failure",
                onActionClick: () => {
                  setActiveWorkspace(workspace);
                  openDeleteModal(true);
                },
              },
            ],
          };
        }),
      );
    }
  }, [
    isQueryLoading,
    queryData,
    activeWorkspace,
    isLoading,
    setActiveWorkspace,
  ]);

  if (userSession == null) {
    return <DefaultSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-text sm:text-2xl">
            {t("workspace:my_workspaces")}
          </h2>
          <p className="text-secondary text-base pt-2 pb-4 dark:text-secondary">
            {t("workspace:description")}
          </p>
        </div>
        <Button
          color="light"
          onClick={() => void handleCreateWorkspace()}
          disabled={isCreating}
        >
          {isCreating ? (
            <Spinner size="sm" className="mr-2" />
          ) : (
            <HiOutlinePlus className="mr-2 h-5 w-5" />
          )}
          {t("workspace:new_workspace")}
        </Button>
      </div>

      <div className="flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow text:white">
              {isLoading ? (
                <TableSkeleton />
              ) : (
                <StandardTable
                  header={[
                    {
                      id: "name",
                      children: t("workspace:workspaces.name"),
                      showOnSmallScreen: true,
                    },
                    {
                      id: "createdAt",
                      children: t("common:created_at"),
                    },
                  ]}
                  hasActions={true}
                  rows={tableRows}
                  compact={true}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <WorkspaceModal
        data={activeWorkspace ?? WorkspaceAccess.default()}
        isShowing={isUpsertModalOpen}
        onConfirm={(formData: IWorkspaceBase) => {
          // The modal is edit-only now; creating a workspace goes through the
          // onboarding flow (handleCreateWorkspace) instead.
          if (activeWorkspace) {
            updateWorkspace(
              userSession?.token.accessToken,
              activeWorkspace.workspaceId ?? "",
              formData,
            );
            setActiveWorkspace({
              ...activeWorkspace,
              workspaceName: formData.name,
            });
          }
          openUpsertodal(false);
          setLoading(true);
        }}
        onCancel={() => openUpsertodal(false)}
      />

      <DialogForm
        title={t("workspace:delete_workspace")}
        show={isDeleteModalOpen}
        toggleModal={() => openDeleteModal(false)}
        showHeader={false}
        dismissible
        buttonConfirmColor="failure"
        confirmButton={() => {
          deleteWorkspace(
            userSession?.token.accessToken,
            activeWorkspace?.workspaceId ?? "",
          );
          openDeleteModal(false);
          setLoading(true);
        }}
        buttonConfirmText={t("common:confirm")}
        buttonConfirmPrependIcon={<HiTrash className="mr-2 h-4 w-4" />}
        buttonCloseText={t("common:close")}
      />
    </div>
  );
};

export default MyWorkspacesSettingsContent;
